import React, { useEffect, useRef, useState } from "react";
import { useMinimizedTabsStore } from "../stores/minimizedTabs";
import { IoCloseOutline } from "react-icons/io5";


export const BottomRevealTray = () => {
    const minimizedTabsStore = useMinimizedTabsStore();

    return (
        <BottomRevealTrayLogic
            hotspotHeight={20}
            trayHeight={60}
            className="bg-slate-900 text-white rounded-md"
            children={
                <div className="flex flex-row items-center h-full w-full gap-2 px-2 overflow-x-scroll scrollbar-none">
                    {minimizedTabsStore.tabs.map((tab) => {
                        const panels = tab.panels.join(", ");
                        return (
                            <div
                                key={tab.name}
                                className="bg-slate-700 hover:bg-slate-600 text-sm p-1 rounded-sm cursor-pointer flex gap-2 items-center"
                                onClick={() => {
                                    minimizedTabsStore.restoreTab(tab.name);
                                }}
                            >
                                {panels}

                                <IoCloseOutline
                                    className="hover:bg-slate-900 rounded-md"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        tab.close();
                                        minimizedTabsStore.deleteTab(tab.name);
                                    }}

                                    size={18}
                                />
                            </div>
                        );
                    })}
                </div>
            }
        />
    );
};



type BottomRevealTrayProps = {
  hotspotHeight?: number; // px from bottom that triggers open
  trayHeight?: number; // px height of the tray itself
  children: React.ReactNode;
  className?: string;
};

function BottomRevealTrayLogic({
  hotspotHeight = 20,
  trayHeight = 64,
  children,
  className,
}: BottomRevealTrayProps) {
  const [open, setOpen] = useState(false);
  const [hoveringTray, setHoveringTray] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const openNow = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };

  const scheduleClose = (delay = 150) => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      if (!hoveringTray) setOpen(false);
    }, delay);
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const fromBottom = window.innerHeight - e.clientY;
      if (fromBottom <= hotspotHeight) {
        openNow();
      } else if (!hoveringTray) {
        scheduleClose(120);
      }
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [hotspotHeight, hoveringTray]);

  // Optional: close when mouse leaves the window entirely
  useEffect(() => {
    const onLeave = () => scheduleClose(200);
    window.addEventListener("mouseleave", onLeave);
    return () => window.removeEventListener("mouseleave", onLeave);
  }, []);

  return (
    <div
      onMouseEnter={() => {
        setHoveringTray(true);
        openNow();
      }}
      onMouseLeave={() => {
        setHoveringTray(false);
        scheduleClose(150);
      }}
      style={{
        position: "fixed",
        left: 12,
        right: 12,
        bottom: 12,
        height: trayHeight,
        transform: open ? "translateY(0)" : `translateY(${trayHeight + 24}px)`,
        transition: "transform 160ms ease",
        zIndex: 9999,
        boxShadow: "0 10px 30px rgba(0,0,0,.15)",
        pointerEvents: "auto",
        // If you prefer Tailwind, replace with classes instead of inline styles.
      }}
      className={className}
    >
      {children}
    </div>
  );
}
